import { useMsal } from "@azure/msal-react";
import { Alert, Button, CircularProgress, Link, Snackbar, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useGateApi } from "../Api/useGateApi";
import { ApiException, AuthGateApiClient, AuthLocalRequest, AuthMicrosoftRequest, StorePluginSessionTokensRequest } from "../Api/ApiServerHooks";
import { BrandingGateApiClient } from "../Api/BrandingGateApiClient";
import Passwordbox from "../Shared/PasswordBox";
import ChooseTenantDialog from "./ChooseTenantDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ResetPasswordDialog from "./ResetPasswordDialog";
import { getMicrosoftLoginAccount, microsoftAccountKindKey, rememberMicrosoftLoginPage, signOutMicrosoftAccount } from "./MicrosoftAccount";
import { useGateMainContext } from "../GateMainContext";
import TextFieldMakeAnimationStartHandler from "../Shared/TextFieldMakeAnimationStartHandler";
import { useTranslation } from "react-i18next";
import LanguagePicker from "../Shared/LanguagePicker";

interface LoginProps {
    externalLogin?: boolean;
}

export default function Login(props: LoginProps) {
    const { instance, accounts } = useMsal();
    const microsoftAccount = getMicrosoftLoginAccount(accounts);
    const theme = useTheme();
    const biggerThan700 = useMediaQuery(theme.breakpoints.up(700));
    const [name, setName] = useState("");
    const params = useParams();
    const { login, logo, branding, setPreLoginBranding } = useGateMainContext();
    const tenantSlug = params.tenantSlug as string | undefined;
    const [slugNotFound, setSlugNotFound] = React.useState(false);
    const { unauthorizedApi: brandingApi } = useGateApi(BrandingGateApiClient);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = React.useState("");
    const [userId, setUserId] = React.useState<number>(0);
    const [loginError, setLoginError] = React.useState("");
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogReset, setDialogResetOpen] = React.useState(false);
    const [dialogPassOpen, setDialogPassOpen] = React.useState(false);
    const [microsoftLogin, setMicrosoftLogin] = React.useState(false);
    const [accessTokenMS, setAccessTokenMS] = React.useState<string>();
    const [changePassAlertOpen, setChangePassAlertOpen] = React.useState(false);
    const [isUserNameAutofilled, setIsUserNameAutofilled] = React.useState(false);
    const [passwordAutoFilled, setPasswordAutoFilled] = React.useState(false);
    const [checkingAuthentication, setCheckingAuthentication] = React.useState(props.externalLogin !== true);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const { unauthorizedApi, authorizedApi } = useGateApi(AuthGateApiClient);

    useEffect(() => {
        if (props.externalLogin === true) {
            setCheckingAuthentication(false);
            return;
        }

        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!token || !refreshToken) {
            setCheckingAuthentication(false);
            return;
        }

        let isMounted = true;
        setCheckingAuthentication(true);

        authorizedApi?.isAuthed().then((isAuthed) => {
            if (!isMounted) return;

            if (isAuthed) {
                navigate('/portal', { replace: true });
                return;
            }

            localStorage.removeItem('token');
            localStorage.removeItem('token_expires');
            localStorage.removeItem('refresh_token');
            setCheckingAuthentication(false);
        }).catch(() => {
            if (!isMounted) return;

            localStorage.removeItem('token');
            localStorage.removeItem('token_expires');
            localStorage.removeItem('refresh_token');
            setCheckingAuthentication(false);
        });

        return () => {
            isMounted = false;
        };
    }, [authorizedApi, navigate, props.externalLogin]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mail = queryParams.get('email');

        if (mail)
            setUserName(mail);
    }, [location]);
    useEffect(() => {
        if (params.lang)
            i18n.changeLanguage(params.lang);
    }, [params.lang, i18n]);

    // Resolve tenant-specific branding from a /login/:tenantSlug URL so the correct logo,
    // name and colors are shown before the user authenticates. Unknown slugs fall back to
    // the default branding with a subtle hint; they never block login.
    useEffect(() => {
        if (!tenantSlug) {
            setSlugNotFound(false);
            return;
        }

        let active = true;
        brandingApi.getBrandingBySlug(tenantSlug)
            .then((resolved) => {
                if (!active) return;
                setPreLoginBranding(resolved);
                setSlugNotFound(resolved === null);
            })
            .catch(() => {
                if (!active) return;
                setPreLoginBranding(null);
                setSlugNotFound(false);
            });

        return () => { active = false; };
    }, [tenantSlug, brandingApi, setPreLoginBranding]);

    const loginLocal = async (tenantId?: number) => {
        unauthorizedApi.authLocal(new AuthLocalRequest({ username: userName, password: password, tenantId: tenantId })).then((v) => {

            if (v.id && v.tenantId) {
                localStorage.removeItem('temp_tenant_token');
                localStorage.setItem('token', v.token!);
                localStorage.setItem('token_expires', v.tokenExpiration?.toString()!);
                localStorage.setItem('refresh_token', v.refreshToken!);
                login(v);
                if (v.resetPassword === true)
                    setDialogPassOpen(true);
                else {
                    if (props.externalLogin !== true)
                        navigate('/portal');
                    else {
                        authorizedApi?.storePluginLoginSessionTokens(new StorePluginSessionTokensRequest({
                            accessToken: v.token!,
                            refreshToken: v.refreshToken!,
                            expires: v.tokenExpiration!
                        })).then((session) => {
                            window.location.href = params.server + '/gateredirect/' + encodeURIComponent(params.redirect ?? '') + '/' + i18n.language + '/' + session.sessionId;
                        })
                    }
                }
            }
            else if (v.id) {
                localStorage.setItem('temp_tenant_token', v.token!);
                if (v.id)
                    setUserId(v.id);
                // delay(400).then(() => {

                setDialogOpen(true);
                // });

            }
        }).catch((e: ApiException) => {
            setPassword("");
            //@ts-ignore
            setLoginError(e.response.Reason);
        });
    }

    const loginMS = async (accessToken: string, tenantId?: number) => {
        unauthorizedApi.authMicrosoft(new AuthMicrosoftRequest({ tenantId: tenantId, token: accessToken })).then((v) => {
            localStorage.setItem('token', v.token!);
            localStorage.setItem('token_expires', v.tokenExpiration?.toString()!);
            localStorage.setItem('refresh_token', v.refreshToken!);
            if (v.id !== undefined && v.tenantId !== undefined) {
                if (props.externalLogin !== true)
                    navigate('/portal');
                else {
                    authorizedApi?.storePluginLoginSessionTokens(new StorePluginSessionTokensRequest({
                        accessToken: v.token!,
                        refreshToken: v.refreshToken!,
                        expires: v.tokenExpiration!
                    })).then((session) => {
                        window.location.href = localStorage.getItem('redirect-server')! + '/gateredirect/' + params.redirect + '/' + i18n.language + '/' + session.sessionId;
                    })
                }
            }
            else if (v.id !== undefined) {
                setUserId(v.id);
                setDialogOpen(true);
            }
        }).catch((e: ApiException) => {
            //@ts-ignore
            setLoginError(e.response.Reason);
        });
    }

    // i18n.changeLanguage('cs');
    return (
        <div className='container'>

            <Snackbar open={loginError !== ""} autoHideDuration={3000} onClose={() => setLoginError("")}>
                <Alert onClose={() => setLoginError("")} severity="error" sx={{ width: '100%' }}>
                    Přihlášení se nezdařilo: {loginError}
                </Alert>
            </Snackbar>
            <Snackbar open={changePassAlertOpen} autoHideDuration={3000} onClose={() => setChangePassAlertOpen(false)}>
                <Alert onClose={() => setChangePassAlertOpen(false)} severity="warning" sx={{ width: '100%' }}>
                    Pro pokračování je nutné změnit heslo
                </Alert>
            </Snackbar>
            <video className='videoTag' style={{ width: '100%', height: '100vh', objectFit: 'fill' }} autoPlay={biggerThan700} loop muted>
                <source src={'https://res.cloudinary.com/duffqpbsu/video/upload/v1617831108/forestry/header.mp4#t=0.001'} type='video/mp4' />
            </video>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%', background: theme.palette.background.default, position: 'absolute', height: '52px', top: '0', textAlign: 'center', boxShadow: '0px 10px 24px rgba(0, 0, 0, 0.12)'
            }}>
                <div></div>
                <div style={{ display: 'flex', textAlign: 'center', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant='h5' fontWeight='bold' color='primary' margin={1}>{branding?.brandName?.trim() || 'SG WebTools'}</Typography>
                    <Typography variant='h5' fontWeight='bold' color='text.primary'>{t('LoginName')}</Typography>
                    <img src={logo} style={{ height: '32px', width: 'auto', minWidth: '92px', maxWidth: '260px', marginLeft: '28px' }} />
                </div>
                <div style={{ marginRight: '20px' }}><LanguagePicker showText /></div>
            </div>
            <div className="centered-div" >
                {checkingAuthentication ? <CircularProgress /> : <>
                    <Typography variant='h4' color='primary' sx={{ margin: '8px', fontWeight: 'bolder' }} >{t('LoginName')}</Typography>
                    <TextField
                        slotProps={{
                            htmlInput: {
                                onAnimationStart: TextFieldMakeAnimationStartHandler(setIsUserNameAutofilled),
                            },
                            inputLabel: {
                                shrink: userName !== "" || isUserNameAutofilled
                            }
                        }}
                        id="outlined-basic"
                        label={t('EnterEmail')}
                        className="text-field"
                        sx={{ marginBottom: '12px', marginTop: '16px' }}
                        value={userName}
                        onChange={(e) => { setUserName(e.currentTarget.value); setIsUserNameAutofilled(false); }}
                        variant="outlined" />
                    <Passwordbox password={password}
                        label={t('EnterPassword')}
                        classNameInput='text-field'
                        onEnter={() => { setMicrosoftLogin(false); loginLocal(); }}
                        sx={{ marginBottom: '12px' }}
                        onPasswordChange={(e) => { setPassword(e); setPasswordAutoFilled(false); }}></Passwordbox>
                    <div style={{ width: '100%', marginTop: '8px' }}>
                        <Button
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'primary.dark',

                                },
                            }}
                            disabled={(password === "" || userName === "") && (!isUserNameAutofilled && !passwordAutoFilled)}
                            variant={'contained'}
                            size='large'
                            style={{ width: '100%', height: '52px', marginBottom: '12px' }}
                            onClick={() => { setMicrosoftLogin(false); loginLocal(); }}>
                            <Typography sx={{ marginLeft: '12px', marginTop: '8px', fontWeight: 'bold' }}>{t('Login')}</Typography>
                        </Button>
                        <a href='' onClick={(e) => { e.preventDefault(); setDialogResetOpen(true) }} style={{ marginTop: '0px' }}>{t('ForgotPassword')}</a>
                        <Typography
                            sx={{ marginLeft: '12px', marginTop: '24px', marginBottom: '14px' }}
                            color='secondary'
                            fontSize={13}>{t('OrUse')}</Typography>
                        <div >
                            <Button variant='outlined' style={{ width: '100%', height: '52px' }} onClick={() => {
                                localStorage.setItem('ms-state', 'login');
                                rememberMicrosoftLoginPage(location.pathname);
                                setMicrosoftLogin(true);
                                if (props.externalLogin !== true) {
                                    localStorage.setItem('ms-redirect', '/portal');
                                    navigate('/auth-callback');
                                }
                                else {
                                    localStorage.setItem('tool-login', 'true');
                                    if (params.server) {
                                        localStorage.setItem('redirect-server', params.server);
                                    }
                                    if (params.redirect) {
                                        localStorage.setItem('ms-redirect', params.redirect);
                                    }
                                    navigate('/auth-callback');
                                }
                            }}><div style={{ display: 'flex' }}>
                                    <img width={32} height={32} src={'/microsoft-logo.svg'} />
                                    <Typography sx={{ marginLeft: '12px', marginTop: '4px', fontWeight: 'bold' }}>{t('MicrosoftLogin')}</Typography>
                                </div></Button>
                            {microsoftAccount && (
                                <Typography color='text.secondary' fontSize={13} sx={{ marginTop: '8px', overflowWrap: 'anywhere' }}>
                                    {t('MicrosoftAccount.LoginAs', { account: microsoftAccount.username, kind: t(microsoftAccountKindKey(microsoftAccount)) })}
                                    {' · '}
                                    <Link component='button' fontSize='inherit' sx={{ verticalAlign: 'baseline' }} onClick={() => {
                                        signOutMicrosoftAccount(instance, microsoftAccount).catch((e) => console.log(e));
                                    }}>{t('MicrosoftAccount.SignOut')}</Link>
                                </Typography>
                            )}
                        </div>
                    </div>
                </>}
                {slugNotFound && (
                    <Typography
                        color='text.secondary'
                        fontSize={12}
                        sx={{ marginTop: '16px', opacity: 0.8 }}>
                        {t('OrganizationNotFound', 'Organization not found')}
                    </Typography>
                )}
            </div>
            <ChooseTenantDialog isOpen={dialogOpen} onClose={(r) => { setDialogOpen(false); if (r) { microsoftLogin ? loginMS(accessTokenMS!, r) : loginLocal(r); } }} />
            <ChangePasswordDialog isOpen={dialogPassOpen} onClose={(res) => {
                if (res) {
                    setDialogPassOpen(false);
                    navigate('/portal');
                }
                else
                    setChangePassAlertOpen(true);
            }
            }
            />
            <ResetPasswordDialog isOpen={dialogReset} onClose={() => { setDialogResetOpen(false) }} />
        </div>
    )
}